import { Link } from "react-router-dom";
import { LegalFooter } from "@/components/LegalFooter";
import { useState } from "react";
import { api } from "@/lib/apiClient";
import { Sparkles, ArrowRight, Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      <div className="hidden md:flex flex-col justify-between bg-zinc-900 text-white p-12 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="w-8 h-8 bg-white rounded-md flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-zinc-900" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-xl">NextGen<span className="text-blue-400"> Resolution Experts</span></span>
        </Link>
        <div className="relative">
          <h2 className="font-display font-black tracking-tighter text-5xl leading-[0.95] mb-6">
            Locked out?<br />
            <span className="text-blue-400">We got you.</span>
          </h2>
          <p className="text-zinc-400 max-w-md">
            Enter the email on your account and we'll send a secure link to reset your password. Link expires in 1 hour.
          </p>
        </div>
        <div className="text-xs text-zinc-500 font-mono relative">Secure · Single-use · 1-hour expiry</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition" data-testid="forgot-back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>

          {sent ? (
            <div data-testid="forgot-success">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <MailCheck className="w-7 h-7 text-emerald-600" strokeWidth={2} />
              </div>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-emerald-600 mb-2">Check your inbox</div>
              <h1 className="font-display font-black tracking-tighter text-4xl mb-3">Reset link sent.</h1>
              <p className="text-zinc-500 mb-8 leading-relaxed">
                If an account exists for <b className="text-zinc-900">{email}</b>, we've sent a password reset link.
                Please check your inbox (and spam folder) — the link expires in 1 hour.
              </p>
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 text-sm text-zinc-500 mb-8">
                <b className="text-zinc-900">Didn't get an email?</b> Double-check the address you entered, or try again in a few minutes.
              </div>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="text-sm font-semibold text-blue-600 hover:underline"
                data-testid="forgot-try-again"
              >
                Try a different email →
              </button>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-2">Forgot password</div>
              <h1 className="font-display font-black tracking-tighter text-4xl mb-2">No worries.</h1>
              <p className="text-zinc-500 mb-10">Enter your email and we'll send you a reset link.</p>

              <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none transition"
                    data-testid="forgot-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white font-bold py-4 rounded-full hover:bg-zinc-800 disabled:opacity-50 transition-all hover:-translate-y-0.5"
                  data-testid="forgot-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
              <div className="mt-8 text-sm text-zinc-500 text-center">
                Remembered it?{" "}
                <Link to="/login" className="font-semibold text-blue-600 hover:underline">
                  Sign in
                </Link>
              </div>
            </>
          )}
          <LegalFooter />
        </div>
      </div>
    </div>
  );
}
